//* Misma tasa con la que el API separa el IVA de los accesorios en `wallet-components.ts` (iva-rate.ts).
const IVA_RATE = 0.16

const COVERED_STATUSES = ['PAID', 'ANTICIPATED']

/** Desglose de la renta mensual como lo pinta Cobranza y el estado de cuenta. Montos contractuales, no lo pagado. */
export interface RentBreakdown {
  /** Renta del activo: capital + interés, sin IVA ni accesorios. */
  amount: number
  /** Mensualidad de accesorios financiados: capital + interés, sin IVA. */
  accessories: number
  iva: number
  total: number
  accessoriesDetail: { capital: number; interest: number; iva: number; total: number }
  /** Letra de la que salió el monto; nulo cuando se cayó a la cotización. */
  period: number | null
  date: string | Date | null
  source: 'pending' | 'paid' | 'quotation'
}

/** Cuántas rentas y por cuánto, como se pintan en el resumen de cobranza. */
export interface RentCount {
  count: number
  amount: number
}

/** Lo que Cobranza y el estado de cuenta leen de la tabla de amortización (B-085). */
export interface AmortizationSummary {
  rent: RentBreakdown | null
  /** Fecha de la 1ª letra regular; nula si el arrendamiento aún no tiene tabla. */
  startDate: string | Date | null
  /** Rentas cobradas por letra, sin las anticipadas. */
  collected: RentCount
  /** Rentas anticipadas, que se cobran en el pago inicial y no por letra. */
  anticipated: RentCount
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

/**
 * Una renta anticipada tiene dos formas en la tabla, y las dos cuentan como cubiertas para la cartera:
 * - la del motor de cotización: las últimas letras quedan `PAID` con `rent` en cero
 *   (`formulas.service.ts`), porque el dinero entra en el pago inicial y no toca letras;
 * - la de los guiones de migración y saneamiento: `ANTICIPATED`, con su renta y su `appliedPayment`.
 */
function isAnticipated(p: any): boolean {
  return p.status === 'ANTICIPATED' || (p.status === 'PAID' && !(p.rent > 0))
}

//* Ordenadas por FECHA, como Exigibilidad: en los migrados las parciales se numeran 1.1, 1.2… y por
//* número la letra 1 quedaría delante de rentas que caen meses antes (B-011). Sin fecha, al final.
function chronological(periods: any[] | null | undefined): any[] {
  const byDate = (p: any) => (p?.date ? new Date(p.date).getTime() : Number.POSITIVE_INFINITY)
  return (periods ?? []).filter((p) => p && typeof p === 'object').sort((a, b) => byDate(a) - byDate(b))
}

/**
 * Todo lo que Cobranza y el estado de cuenta leen de la tabla de amortización, en una sola pasada.
 *
 * `termData` es la foto del día en que se cotizó: el editor de saldos (`EDIT_PERIODS`) reescribe las letras
 * y la foto se queda atrás —17 de 48 activos en PROD al 14-oct-2026—. Por eso aquí manda la tabla y la
 * cotización solo se usa cuando el arrendamiento todavía no tiene letras.
 *
 * `rents` es `lease.rents`, que el API deriva de la tabla (`recomputeLeaseAmounts`) contando las
 * anticipadas como cobradas. Aquí se separan solo para pintarlas: la regla de cartera no cambia.
 */
export function summarizeAmortization(periods: any[] | null | undefined, termData?: any, rents?: any): AmortizationSummary {
  const letters = chronological(periods)
  const regular = letters.filter((p) => p.rent > 0 && !isAnticipated(p))
  const anticipated = letters.filter(isAnticipated)

  return {
    rent: pickRent(regular, termData),
    startDate: regular[0]?.date ?? null,
    ...splitCollected(anticipated, termData, rents),
  }
}

/**
 * La renta vigente: la primera letra pendiente y, si ya no queda ninguna, la última pagada.
 *
 * Solo campos contractuales (`capital`, `interest`, `accessories*`, `inputIva`, `rent`). `payment` y
 * `appliedPayment` cambian con cada pago parcial, así que una letra a medio pagar mostraría de menos.
 */
function pickRent(regular: any[], termData: any): RentBreakdown | null {
  const pending = regular.find((p) => !COVERED_STATUSES.includes(p.status))
  const lastPaid = [...regular].reverse().find((p) => COVERED_STATUSES.includes(p.status))
  const period = pending ?? lastPaid

  if (period) return fromPeriod(period, pending ? 'pending' : 'paid')
  return fromQuotation(termData?.rent, termData?.accessoriesDetail?.financed?.period)
}

/**
 * Separa las anticipadas de las cobradas. Aquí se cuenta DINERO cobrado, no la renta del contrato —esa la da
 * `pickRent`—: «cobradas» del API ya es dinero aplicado, y las anticipadas tienen que sumar en la misma moneda.
 *
 * La del motor (en cero) cobró lo cotizado por letra en el pago inicial, que no toca letras. La `ANTICIPATED`
 * cobró lo aplicado a la letra: puede ser menos que su renta (en R-282 de dev, $53,013.44 de $55,346.33,
 * porque la anticipada se cotizó sin accesorios), y lo que falta ya está en «por cobrar». Su dinero aplicado
 * sale del monto de cobradas para no contarlo dos veces.
 */
function splitCollected(anticipated: any[], termData: any, rents: any): Pick<AmortizationSummary, 'collected' | 'anticipated'> {
  const quoted = termData?.advancedRents
  const quotedPerLetter = quoted?.number > 0 ? (quoted.total ?? 0) / quoted.number : 0
  const anticipatedApplied = anticipated.reduce((s, p) => s + (p.appliedPayment || 0), 0)
  const anticipatedAmount = anticipated.reduce((s, p) => s + (p.rent > 0 ? p.appliedPayment || 0 : quotedPerLetter), 0)
  //* El API solo cuenta como cobrada la anticipada sin saldo (F33): una con `payment` vivo no está en
  //* `rentsCollected` y restarla dejaría el conteo corto.
  const anticipatedCovered = anticipated.filter((p) => !((p.payment || 0) > 0.005)).length

  return {
    collected: {
      count: Math.max(0, (rents?.rentsCollected ?? 0) - anticipatedCovered),
      amount: Math.max(0, round2((rents?.rentsCollectedAmount ?? 0) - anticipatedApplied)),
    },
    anticipated: { count: anticipated.length, amount: round2(anticipatedAmount) },
  }
}

function fromPeriod(p: any, source: 'pending' | 'paid'): RentBreakdown {
  const accCapital = p.accessoriesAmount || 0
  const accInterest = p.accessoriesInterest || 0
  const accessories = round2(accCapital + accInterest)
  const accIva = round2(accessories * IVA_RATE)
  return {
    amount: round2((p.capital || 0) + (p.interest || 0)),
    accessories,
    iva: round2(p.inputIva || 0),
    total: round2(p.rent),
    accessoriesDetail: { capital: round2(accCapital), interest: round2(accInterest), iva: accIva, total: round2(accessories + accIva) },
    period: p.period ?? null,
    date: p.date ?? null,
    source,
  }
}

//* Sin tabla (arrendamiento aún sin letras) queda lo único que hay: lo cotizado.
function fromQuotation(rent: any, accessories: any): RentBreakdown | null {
  if (!rent) return null
  const detail = accessories ?? {}
  return {
    amount: rent.amount ?? 0,
    accessories: rent.accessories ?? 0,
    iva: rent.iva ?? 0,
    total: rent.total ?? 0,
    accessoriesDetail: { capital: detail.amount ?? 0, interest: detail.interest ?? 0, iva: detail.iva ?? 0, total: detail.total ?? 0 },
    period: null,
    date: null,
    source: 'quotation',
  }
}
